// The forge observation renderers (plan 101 slices 07 and 10b): `forge.mr.*`, one
// gitlab.com merge request re-read and graded and the same lane's named refusal, and their
// Gitee twins `forge.pr.*`. Their own module beside `./maintenance-tenancy.ts`, which is at
// the file-size bound and only lists them (the `./installation-permissions.ts` precedent).
// The renders are unchanged; their tests stay in `./maintenance-forge.test.ts`.

import type { TerminalRenderContext, TerminalRendered } from '../../../wire/terminal';
import {
  ForgeMrObservedOutputSchema,
  ForgeMrObserveRefusedOutputSchema,
  ForgePrObservedOutputSchema,
  ForgePrObserveRefusedOutputSchema,
} from '../../audit/payload';

// ─── forge.mr.observed (info) — a GitLab MR re-read and graded, read-only ─────
//
// The output is parsed WHOLE against its writer's schema rather than read field by
// field: a line naming a merge verdict without its MR would be worse than no line.
// A withheld or malformed row renders nothing. The subject is a pull request keyed
// `path!iid`, and `refs` stays empty: `repoFullName` links a GitHub repo route, and
// a GitLab project is not one.
/** One `forge.mr.observed` row as a console line; `null` when its output does not parse. */
export function renderForgeMrObserved(ctx: TerminalRenderContext): TerminalRendered | null {
  const parsed = ForgeMrObservedOutputSchema.safeParse(ctx.row.output);
  if (!parsed.success) return null;
  const { path, iid, mergeVerdict, ciVerdict } = parsed.data;
  const label = `${path}!${iid}`;
  return {
    line: {
      key: 'terminal.forge.mr.observed',
      params: { iid, path, merge: mergeVerdict.kind, ci: ciVerdict },
    },
    subject: { kind: 'pull_request', id: label, label },
    refs: {},
  };
}

// ─── forge.mr.observe_refused (warn) — the same lane, refused by name ─────────
//
// Parsed whole, like its sibling above. The reason enum is closed, so every reason has
// its own sentence (`terminal.forge.mr.observe_refused.<reason>`) naming the next move —
// a maintainer whose token expired reads "reconnect with a new token", not a code. The
// row carries no project path (a refusal may precede any read), so the subject is the
// MR keyed by GitLab's numeric `projectId!iid`, which the REST paths accept as-is.
/** One `forge.mr.observe_refused` row as its reason's sentence; `null` when unparseable. */
export function renderForgeMrObserveRefused(ctx: TerminalRenderContext): TerminalRendered | null {
  const parsed = ForgeMrObserveRefusedOutputSchema.safeParse(ctx.row.output);
  if (!parsed.success) return null;
  const { projectId, mrIid, reason } = parsed.data;
  const label = `${projectId}!${mrIid}`;
  return {
    line: {
      key: `terminal.forge.mr.observe_refused.${reason}`,
      params: { iid: mrIid, projectId },
    },
    subject: { kind: 'pull_request', id: label, label },
    refs: {},
  };
}

// ─── forge.pr.observed (info) — a Gitee PR re-read, read-only (slice 10b) ────
//
// The `forge.mr.observed` posture: parsed whole, a malformed row renders nothing, `refs`
// empty (a Gitee repo is not a GitHub repo route). Subject `owner/repo#number`, Gitee's
// own spelling of a PR. The line names what the read returned: state, Gitee's
// `mergeable`, the rolled-up CI verdict and the reviewer / tester accepts as `n/total`.
/** One `forge.pr.observed` row as a console line; `null` when its output does not parse. */
export function renderForgePrObserved(ctx: TerminalRenderContext): TerminalRendered | null {
  const parsed = ForgePrObservedOutputSchema.safeParse(ctx.row.output);
  if (!parsed.success) return null;
  const { path, number, state, mergeable, ciVerdict, reviewers, testers } = parsed.data;
  const label = `${path}#${number}`;
  return {
    line: {
      key: 'terminal.forge.pr.observed',
      params: {
        number,
        path,
        state,
        mergeable: String(mergeable),
        ci: ciVerdict,
        reviewers: `${reviewers.accepted}/${reviewers.total}`,
        testers: `${testers.accepted}/${testers.total}`,
      },
    },
    subject: { kind: 'pull_request', id: label, label },
    refs: {},
  };
}

// ─── forge.pr.observe_refused (warn) — the same lane, refused by name ─────────
//
// One sentence per closed reason (`terminal.forge.pr.observe_refused.<reason>`). A
// refusal may precede any read, so the subject is the PR keyed by Gitee's numeric
// `repoId#number`.
/** One `forge.pr.observe_refused` row as its reason's sentence; `null` when unparseable. */
export function renderForgePrObserveRefused(ctx: TerminalRenderContext): TerminalRendered | null {
  const parsed = ForgePrObserveRefusedOutputSchema.safeParse(ctx.row.output);
  if (!parsed.success) return null;
  const { repoId, number, reason } = parsed.data;
  const label = `${repoId}#${number}`;
  return {
    line: { key: `terminal.forge.pr.observe_refused.${reason}`, params: { number, repoId } },
    subject: { kind: 'pull_request', id: label, label },
    refs: {},
  };
}
